import { useState, useEffect } from 'react';

/**
 * usePrefersReducedMotion - Tracks the `(prefers-reduced-motion: reduce)` media query.
 *
 * Used by the Institutional Console redesign (DESIGN-SPEC.md §6/§7) to disable
 * shimmer/flash/transition effects for users who request reduced motion —
 * e.g. the command palette's entrance animation and the 400ms live-price flash.
 *
 * @returns {boolean} true if the user has requested reduced motion
 */
function safeMatchMedia() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  // Defensive: some test environments mock matchMedia with an implementation
  // that doesn't return a MediaQueryList-like object — never let this hook
  // throw, since prefers-reduced-motion is a progressive-enhancement signal.
  try {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    return mql && typeof mql.matches === 'boolean' ? mql : null;
  } catch {
    return null;
  }
}

export function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    return safeMatchMedia()?.matches ?? false;
  });

  useEffect(() => {
    const mediaQuery = safeMatchMedia();
    if (!mediaQuery) return undefined;

    const handleChange = (event) => setPrefersReducedMotion(event.matches);

    // Safari <14 only supports addListener/removeListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
    return undefined;
  }, []);

  return prefersReducedMotion;
}

export default usePrefersReducedMotion;
